import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';

import Layout from '../../containers/Layout';
import Navigation from '../../components/Navigation';
import Breadcrumbs from '../../components/Breadcrumbs';
import UserService from '../../services/UserService';
import SuperAdminService from '../../services/SuperAdminService';
import { USER_ROLES } from '../../constants/main';
import FilledButton from '../../components/Buttons/FilledButton';
import OutlinedButton from '../../components/Buttons/OutlinedButton';
import OrganizationForm, { emptyForm, emptyFeatureFlags } from '../../components/OrganizationForm';
import { UiContext } from '../../context/UiContext';
import { useUiStore } from '../../store';
import DotsLoader from '../../components/DotsLoader';

import classes from './styles.module.scss';

export default function OrganizationEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notifySuccess, notifyError } = useContext(UiContext);
  const [formValues, setFormValues] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [featureFlags, setFeatureFlags] = useState(emptyFeatureFlags);
  const [storageQuota, setStorageQuota] = useState('');
  const [seatQuota, setSeatQuota] = useState('');
  const [trialEndsAt, setTrialEndsAt] = useState('');

  const { data: currentUser, isLoading: isLoadingUser } = useQuery(
    'me',
    UserService.getMe
  );

  const {
    data: organization,
    isLoading: isLoadingOrganization,
  } = useQuery(
    ['super-admin-organization', id],
    () => SuperAdminService.getOrganization(id),
    {
      enabled: currentUser?.role === USER_ROLES.SUPER_ADMIN && !!id,
      onSuccess: (data) => {
        setFormValues({
          name: data.name || '',
          billingAddress: data.billingAddress || '',
          city: data.city || '',
          state: data.state || '',
          zip: data.zip || '',
        });
        setFeatureFlags({
          training_paths: Boolean(data.metadata?.feature_flags?.training_paths),
        });
        setStorageQuota(data.metadata?.storage_quota?.toString() || '');
        setSeatQuota(data.metadata?.seat_quota?.toString() || '');
        setTrialEndsAt(data.metadata?.trial?.endsAt || '');
      },
    }
  );

  const setBreadcrumbsOptions = useUiStore(
    (state) => state.setBreadcrumbsOptions
  );

  useEffect(() => {
    setBreadcrumbsOptions([
      { label: 'Super Admin', to: '/super-admin' },
      { label: organization?.name || 'Organization', to: `/super-admin/organizations/${id}` },
      { label: 'Edit', to: '#' },
    ]);
  }, [setBreadcrumbsOptions, organization, id]);

  const validateForm = useCallback((values) => {
    const errors = {};
    if (!values.name.trim()) {
      errors.name = 'Name is required';
    } else if (values.name.trim().length > 50) {
      errors.name = 'Must be 50 characters or less';
    }

    if (!values.billingAddress.trim()) {
      errors.billingAddress = 'Billing address is required';
    } else if (values.billingAddress.trim().length > 500) {
      errors.billingAddress = 'Must be 500 characters or less';
    }

    if (!values.city.trim()) {
      errors.city = 'City is required';
    }

    if (!values.state.trim()) {
      errors.state = 'State is required';
    }

    const zipRegex = /^[0-9-]{5,10}$/;
    if (!values.zip.trim()) {
      errors.zip = 'ZIP code is required';
    } else if (!zipRegex.test(values.zip.trim())) {
      errors.zip = 'ZIP must be 5-10 digits';
    }
    return errors;
  }, []);

  const handleFormChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleFeatureFlagChange = (flag, value) => {
    setFeatureFlags((prev) => ({ ...prev, [flag]: value }));
  };

  const { mutateAsync: updateOrganization, isLoading: isUpdating } =
    useMutation(({ organizationId, payload }) =>
      SuperAdminService.updateOrganization(organizationId, payload)
    );

  const handleSubmit = async () => {
    const errors = validateForm(formValues);
    setFormErrors(errors);
    if (Object.keys(errors).length) {
      return;
    }

    try {
      await updateOrganization({
        organizationId: id,
        payload: {
          ...formValues,
          feature_flags: featureFlags,
          storage_quota: storageQuota && storageQuota.trim() ? parseInt(storageQuota.trim(), 10) : null,
          seat_quota: seatQuota && seatQuota.trim() ? parseInt(seatQuota.trim(), 10) : null,
          trial_ends_at: trialEndsAt ? new Date(trialEndsAt).toISOString() : null,
        },
      });
      notifySuccess('Organization updated');
      navigate(`/super-admin/organizations/${id}`);
    } catch (error) {
      notifyError(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to update organization'
      );
    }
  };

  const handleCancel = () => {
    navigate(`/super-admin/organizations/${id}`);
  };

  if (currentUser?.role !== USER_ROLES.SUPER_ADMIN) {
    return null;
  }

  if (isLoadingUser || isLoadingOrganization) {
    return (
      <Layout hasUserBlock>
        <div className={classes.loaderContainer}>
          <DotsLoader />
        </div>
      </Layout>
    );
  }

  if (!organization) {
    return (
      <Layout hasUserBlock>
        <div className={classes.emptyState}>
          Organization not found
        </div>
      </Layout>
    );
  }

  return (
    <Layout hasUserBlock>
      <div className={classes.OrganizationEditPage}>
        <Navigation />
        <div className={classes.container}>
          <div className={classes.breadcrumbsContainer}>
            <Breadcrumbs
              breadcrumbsOptions={[
                { label: 'Super Admin', to: '/super-admin' },
                { label: organization.name, to: `/super-admin/organizations/${id}` },
                { label: 'Edit', to: '#' },
              ]}
            />
          </div>

          <OrganizationForm
            formValues={formValues}
            formErrors={formErrors}
            featureFlags={featureFlags}
            storageQuota={storageQuota}
            seatQuota={seatQuota}
            trialEndsAt={trialEndsAt}
            onFormChange={handleFormChange}
            onFeatureFlagChange={handleFeatureFlagChange}
            onStorageQuotaChange={setStorageQuota}
            onSeatQuotaChange={setSeatQuota}
            onTrialEndsAtChange={setTrialEndsAt}
          />

          <div className={classes.actions}>
            <OutlinedButton
              onClick={handleCancel}
              disabled={isUpdating}
              style={{ minWidth: '280px' }}
            >
              Cancel
            </OutlinedButton>
            <FilledButton
              type="button"
              onClick={handleSubmit}
              disabled={isUpdating}
              style={{ minWidth: '280px' }}
            >
              {isUpdating ? 'Saving…' : 'Save changes'}
            </FilledButton>
          </div>
        </div>
      </div>
    </Layout>
  );
}
